/**
 * Stock Management Page
 * 
 * Studio inventory management for tracking supplies and materials.
 * Access: can_access_commission = true users
 * 
 * Features:
 * - View stock items list
 * - Add/Edit stock items
 * - Quick quantity adjustment (+/-) 
 * - Category filtering
 * - Low stock warnings
 * - Search functionality
 */
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { UserContext } from '../contexts/UserContext';
import { getThemeForPage } from '../styles/themes';
import { useTranslation } from '../i18n/useTranslation';
import { hasCommissionAccess } from '../utils/permissionConfig';
import styles from '../styles/StockStyles.module.css';
import commonStyles from '../styles/CommonStyles.module.css';
import * as stockApi from '../services/stockApi';

export default function StockManagementPage() {
    const { userInfo } = useContext(UserContext);
    const currentTheme = getThemeForPage('/stock-management');
    const { t } = useTranslation();

    // Access control (same as commission management)
    const hasAccess = hasCommissionAccess(userInfo);

    // PWA theme color
    useEffect(() => {
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) {
            themeColorMeta.setAttribute('content', '#fffdf8');
        }
    }, []);

    // State
    const [stocks, setStocks] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name'); // name, quantity, updated, category, lowStock
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStock, setEditingStock] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        quantity: '',
        category_id: null,
        image_url: '',
        supplier: '',
        price_ht: '',
        tva_rate: '20',
        min_stock_level: '',
    });

    // Category modal state
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [editingCategoryName, setEditingCategoryName] = useState('');

    // Scroll position ref
    const savedScrollY = React.useRef(0);

    // Load categories and stocks
    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [categoriesData, stocksData] = await Promise.all([
                stockApi.getCategories(),
                stockApi.getStocks()
            ]);
            setCategories(categoriesData);
            setStocks(stocksData);
        } catch (err) {
            console.error('Failed to load data:', err);
            setError(t('stock.loadError'));
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    // Load data on mount
    useEffect(() => {
        if (hasAccess) {
            loadData();
        }
    }, [hasAccess, loadData]);

    // Filtered and sorted stocks
    const filteredStocks = useMemo(() => {
        let result = stocks.filter(stock => {
            // Category filter
            if (selectedCategory !== 'all') {
                const catId = parseInt(selectedCategory);
                if (stock.category_id !== catId) {
                    return false;
                }
            }
            // Search filter
            if (searchQuery) {
                return stock.name.toLowerCase().includes(searchQuery.toLowerCase());
            }
            return true;
        });

        // Sort
        result.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'quantity':
                    return a.quantity - b.quantity;
                case 'updated':
                    return new Date(b.last_updated || 0) - new Date(a.last_updated || 0);
                case 'category':
                    const getCatName = (catId) => {
                        if (!catId) return t('stock.uncategorized');
                        const cat = categories.find(c => c.id === catId);
                        return cat?.name || t('stock.uncategorized');
                    };
                    const catA = a.category_name || getCatName(a.category_id);
                    const catB = b.category_name || getCatName(b.category_id);
                    return catA.localeCompare(catB);
                case 'lowStock':
                    const aLow = a.min_stock_level && a.quantity <= a.min_stock_level ? 0 : 1;
                    const bLow = b.min_stock_level && b.quantity <= b.min_stock_level ? 0 : 1;
                    return aLow - bLow;
                default:
                    return 0;
            }
        });

        return result;
    }, [categories, searchQuery, selectedCategory, sortBy, stocks, t]);

    // Check if item is low stock
    const isLowStock = (stock) => {
        if (!stock.min_stock_level) return false;
        return stock.quantity <= stock.min_stock_level;
    };

    // Get category name by id
    const getCategoryName = useCallback((categoryId) => {
        if (!categoryId) return t('stock.uncategorized');
        const cat = categories.find(c => c.id === categoryId);
        return cat?.name || t('stock.uncategorized');
    }, [categories, t]);

    // Quantity adjustment
    const handleAdjustQuantity = async (stockId, delta) => {
        try {
            const result = await stockApi.adjustQuantity(stockId, delta);
            // Update local state
            setStocks(prev => prev.map(stock => {
                if (stock.id === stockId) {
                    return {
                        ...stock,
                        quantity: result.quantity,
                        last_updated: result.last_updated,
                    };
                }
                return stock;
            }));
        } catch (err) {
            console.error('Failed to adjust quantity:', err);
            alert('Failed to update quantity');
        }
    };

    // Direct quantity input with debounce
    const handleQuantityInput = async (stockId, value) => {
        const currentStock = stocks.find(s => s.id === stockId);
        if (!currentStock) return;

        const newQuantity = Math.max(0, parseInt(value) || 0);
        const change = newQuantity - currentStock.quantity;

        if (change === 0) return;

        try {
            const result = await stockApi.adjustQuantity(stockId, change);
            setStocks(prev => prev.map(stock => {
                if (stock.id === stockId) {
                    return {
                        ...stock,
                        quantity: result.quantity,
                        last_updated: result.last_updated,
                    };
                }
                return stock;
            }));
        } catch (err) {
            console.error('Failed to update quantity:', err);
            // Reload to get correct state
            loadData();
        }
    };

    // Open add modal
    const openAddModal = () => {
        setEditingStock(null);
        setFormData({
            name: '',
            quantity: '',
            category_id: categories.find(c => c.name !== 'Uncategorized')?.id || null,
            image_url: '',
            supplier: '',
            price_ht: '',
            tva_rate: '20',
            min_stock_level: '',
        });
        setIsModalOpen(true);
    };

    // Open edit modal
    const openEditModal = (stock) => {
        savedScrollY.current = window.scrollY; // Save scroll position
        setEditingStock(stock);
        setFormData({
            name: stock.name,
            quantity: stock.quantity.toString(),
            category_id: stock.category_id,
            image_url: stock.image_url || '',
            supplier: stock.supplier || '',
            price_ht: stock.price_ht?.toString() || '',
            tva_rate: stock.tva_rate?.toString() || '20',
            min_stock_level: stock.min_stock_level?.toString() || '',
        });
        setIsModalOpen(true);
    };

    // Close modal
    const closeModal = () => {
        setIsModalOpen(false);
        setEditingStock(null);
    };

    // Save stock
    const saveStock = async () => {
        if (!formData.name || !formData.quantity) {
            alert('Please fill required fields (Name, Quantity)');
            return;
        }

        const editedStockId = editingStock?.id;
        setIsSaving(true);
        try {
            const stockData = {
                name: formData.name,
                quantity: parseInt(formData.quantity) || 0,
                category_id: formData.category_id || null,
                image_url: formData.image_url || null,
                supplier: formData.supplier || null,
                price_ht: formData.price_ht ? parseFloat(formData.price_ht) : null,
                tva_rate: formData.tva_rate ? parseFloat(formData.tva_rate) : 20,
                min_stock_level: formData.min_stock_level ? parseInt(formData.min_stock_level) : null,
            };

            if (editingStock) {
                await stockApi.updateStock(editingStock.id, stockData);
            } else {
                await stockApi.createStock(stockData);
            }

            await loadData();
            closeModal();

            // Scroll to edited item
            if (editedStockId) {
                setTimeout(() => {
                    const element = document.getElementById(`stock-${editedStockId}`);
                    if (element) {
                        element.scrollIntoView({ behavior: 'instant', block: 'center' });
                    }
                }, 100);
            }
        } catch (err) {
            console.error('Failed to save stock:', err);
            alert(t('stock.saveError'));
        } finally {
            setIsSaving(false);
        }
    };

    // Delete stock
    const handleDeleteStock = async (stockId) => {
        if (!window.confirm(t('stock.confirmDelete'))) return;

        try {
            await stockApi.deleteStock(stockId);
            await loadData();
        } catch (err) {
            console.error('Failed to delete:', err);
            alert(t('stock.deleteError'));
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).replace(/\//g, '-');
    };

    // Get stock count per category
    const getCategoryStockCount = useCallback((categoryId) => {
        return stocks.filter(s => s.category_id === categoryId).length;
    }, [stocks]);

    // Add new category
    const handleAddCategory = async () => {
        if (!newCategoryName.trim()) return;
        try {
            await stockApi.createCategory(newCategoryName.trim());
            setNewCategoryName('');
            await loadData();
        } catch (err) {
            console.error('Failed to add category:', err);
            alert('Failed to add category');
        }
    };

    // Update category name
    const handleUpdateCategory = async (id, newName) => {
        if (!newName.trim()) return;
        try {
            await stockApi.updateCategory(id, newName.trim());
            setEditingCategoryId(null);
            setEditingCategoryName('');
            await loadData();
        } catch (err) {
            console.error('Failed to update category:', err);
            alert('Failed to update category');
        }
    };

    // Delete category
    const handleDeleteCategory = async (id) => {
        const count = getCategoryStockCount(id);
        if (count > 0) {
            if (!window.confirm(`This category has ${count} items. Delete anyway?`)) return;
        }
        try {
            await stockApi.deleteCategory(id);
            await loadData();
        } catch (err) {
            console.error('Failed to delete category:', err);
            alert('Failed to delete category');
        }
    };

    // No access
    if (!hasAccess) {
        return (
            <div className={styles.noAccess}>
                {t('stock.noAccess')}
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {/* Header */}
            <h1 className={styles.pageTitle} style={{ color: currentTheme?.activeColor }}>
                {t('stock.title')}
            </h1>

            {/* Category tabs */}
            <div className={styles.categoryTabs}>
                <button
                    className={styles.categoryManageBtn}
                    onClick={() => setIsCategoryModalOpen(true)}
                    title="Manage Categories"
                >
                    ⚙
                </button>
                <button
                    className={`${styles.categoryTab} ${selectedCategory === 'all' ? styles.activeTab : ''}`}
                    onClick={() => setSelectedCategory('all')}
                >
                    {t('stock.all')}
                </button>
                {categories.map(cat => (
                    <button
                        key={cat.id}
                        className={`${styles.categoryTab} ${selectedCategory === cat.id ? styles.activeTab : ''}`}
                        onClick={() => setSelectedCategory(cat.id)}
                    >
                        {cat.name}
                    </button>
                ))}
            </div>

            {/* Search + Sort + Add button */}
            <div className={styles.toolbarRow}>
                <input
                    type="text"
                    className={styles.searchInput}
                    placeholder={t('stock.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className={styles.addButton} onClick={openAddModal}>
                    +
                </button>
            </div>

            {/* Sort tabs */}
            <div className={styles.sortTabs}>
                {[
                    { key: 'name', label: 'A-Z' },
                    { key: 'quantity', label: 'QTY' },
                    { key: 'updated', label: 'NEW' },
                    { key: 'category', label: 'CAT' },
                    { key: 'lowStock', label: 'LOW' },
                ].map(opt => (
                    <button
                        key={opt.key}
                        className={`${styles.sortTab} ${sortBy === opt.key ? styles.activeSortTab : ''}`}
                        onClick={() => setSortBy(opt.key)}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {/* Error message */}
            {error && <div className={styles.error}>{error}</div>}

            {/* Stock list */}
            {isLoading ? (
                <div className={styles.loading}>Loading...</div>
            ) : filteredStocks.length === 0 ? (
                <div className={styles.noData}>{t('stock.noData')}</div>
            ) : (
                <div className={styles.listContainer}>
                    {filteredStocks.map(stock => (
                        <div
                            key={stock.id}
                            id={`stock-${stock.id}`}
                            className={`${styles.stockCard} ${isLowStock(stock) ? styles.lowStock : ''}`}
                        >
                            {/* Image placeholder */}
                            <div className={styles.stockImage}>
                                {stock.image_url ? (
                                    <img src={stock.image_url} alt={stock.name} />
                                ) : (
                                    <span className={styles.noImage}>−</span>
                                )}
                            </div>

                            {/* Info */}
                            <div className={styles.stockInfo} onClick={() => openEditModal(stock)}>
                                <div className={styles.stockName}>
                                    {stock.name}
                                    {isLowStock(stock) && (
                                        <span className={styles.lowStockBadge}>{t('stock.lowStock')}</span>
                                    )}
                                </div>
                                <div className={styles.stockMeta}>
                                    <span>{stock.category_name || getCategoryName(stock.category_id)}</span>
                                    <span>{formatDate(stock.last_updated)}</span>
                                </div>
                            </div>

                            {/* Quantity controls */}
                            <div className={styles.quantityControls}>
                                <button
                                    className={styles.quantityBtn}
                                    onClick={() => handleAdjustQuantity(stock.id, -1)}
                                >
                                    −
                                </button>
                                <input
                                    type="number"
                                    className={styles.quantityInput}
                                    value={stock.quantity}
                                    onChange={(e) => handleQuantityInput(stock.id, e.target.value)}
                                    min="0"
                                />
                                <button
                                    className={styles.quantityBtn}
                                    onClick={() => handleAdjustQuantity(stock.id, 1)}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className={commonStyles.modalOverlay} onClick={closeModal}>
                    <div className={commonStyles.modal} onClick={e => e.stopPropagation()}>
                        <div className={commonStyles.modalHeader}>
                            <h3>{editingStock ? t('stock.editItem') : t('stock.addItem')}</h3>
                            <button className={commonStyles.closeButton} onClick={closeModal}>×</button>
                        </div>
                        <div className={styles.modalBody}>
                            {/* Name */}
                            <div className={styles.formGroup}>
                                <label>{t('stock.name')} *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className={styles.input}
                                />
                            </div>

                            {/* Quantity */}
                            <div className={styles.formGroup}>
                                <label>{t('stock.quantity')} *</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.quantity}
                                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                    className={styles.input}
                                />
                            </div>

                            {/* Category */}
                            <div className={styles.formGroup}>
                                <label>{t('stock.category')}</label>
                                <select
                                    value={formData.category_id || ''}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        category_id: e.target.value ? parseInt(e.target.value) : null
                                    })}
                                    className={styles.select}
                                >
                                    <option value="">{t('stock.uncategorized')}</option>
                                    {categories.filter(c => c.name !== 'Uncategorized').map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Supplier */}
                            <div className={styles.formGroup}>
                                <label>{t('stock.supplier')}</label>
                                <input
                                    type="text"
                                    value={formData.supplier}
                                    onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                                    className={styles.input}
                                />
                            </div>

                            {/* Price HT / TVA in row */}
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label>{t('stock.priceHT')} (€)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.price_ht}
                                        onChange={(e) => setFormData({ ...formData, price_ht: e.target.value })}
                                        className={styles.input}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label>{t('stock.tvaRate')}</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="100"
                                        value={formData.tva_rate}
                                        onChange={(e) => setFormData({ ...formData, tva_rate: e.target.value })}
                                        className={styles.input}
                                    />
                                </div>
                            </div>

                            {/* Min Stock Level */}
                            <div className={styles.formGroup}>
                                <label>{t('stock.minStock')}</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.min_stock_level}
                                    onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })}
                                    className={styles.input}
                                />
                            </div>

                            {/* Buttons */}
                            <div className={styles.modalButtons}>
                                {editingStock && (
                                    <button
                                        className={styles.deleteBtn}
                                        onClick={() => {
                                            handleDeleteStock(editingStock.id);
                                            closeModal();
                                        }}
                                    >
                                        {t('stock.delete')}
                                    </button>
                                )}
                                <div className={styles.buttonSpacer} />
                                <button
                                    className={styles.cancelBtn}
                                    onClick={closeModal}
                                    disabled={isSaving}
                                >
                                    {t('stock.cancel')}
                                </button>
                                <button
                                    className={styles.saveBtn}
                                    onClick={saveStock}
                                    disabled={isSaving}
                                >
                                    {isSaving ? '...' : t('stock.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Category Management Modal */}
            {isCategoryModalOpen && (
                <div className={commonStyles.modalOverlay} onClick={() => setIsCategoryModalOpen(false)}>
                    <div className={commonStyles.modal} onClick={e => e.stopPropagation()}>
                        <div className={commonStyles.modalHeader}>
                            <h3>Categories</h3>
                            <button className={commonStyles.closeButton} onClick={() => setIsCategoryModalOpen(false)}>×</button>
                        </div>
                        <div className={styles.modalBody}>
                            {/* Add new category */}
                            <div className={styles.categoryAddRow}>
                                <input
                                    type="text"
                                    className={styles.input}
                                    placeholder="New category..."
                                    value={newCategoryName}
                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                                />
                                <button
                                    className={styles.categoryAddBtn}
                                    onClick={handleAddCategory}
                                    disabled={!newCategoryName.trim()}
                                >
                                    +
                                </button>
                            </div>

                            {/* Category list */}
                            <div className={styles.categoryList}>
                                {categories.map(cat => (
                                    <div key={cat.id} className={styles.categoryItem}>
                                        {editingCategoryId === cat.id ? (
                                            <input
                                                type="text"
                                                className={styles.categoryEditInput}
                                                value={editingCategoryName}
                                                onChange={(e) => setEditingCategoryName(e.target.value)}
                                                onBlur={() => handleUpdateCategory(cat.id, editingCategoryName)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleUpdateCategory(cat.id, editingCategoryName);
                                                    if (e.key === 'Escape') {
                                                        setEditingCategoryId(null);
                                                        setEditingCategoryName('');
                                                    }
                                                }}
                                                autoFocus
                                            />
                                        ) : (
                                            <span
                                                className={styles.categoryName}
                                                onClick={() => {
                                                    setEditingCategoryId(cat.id);
                                                    setEditingCategoryName(cat.name);
                                                }}
                                            >
                                                {cat.name}
                                            </span>
                                        )}
                                        <span className={styles.categoryCount}>
                                            {getCategoryStockCount(cat.id)}
                                        </span>
                                        <button
                                            className={styles.categoryDeleteBtn}
                                            onClick={() => handleDeleteCategory(cat.id)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
